import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert base64url → Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// Safe check for Notification API (not available on iOS Safari)
const getNotificationPermission = () => {
  if (typeof Notification !== 'undefined') {
    return Notification.permission;
  }
  return 'denied';
};

export const usePushNotifications = (city = '') => {
  const [permission, setPermission] = useState(getNotificationPermission);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if already subscribed on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    });
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  }, []);

  const subscribe = useCallback(async () => {
    if (!('PushManager' in window)) {
      alert('Push notifications are not supported in this browser.');
      return false;
    }
    setLoading(true);
    try {
      // Get VAPID public key from backend
      const keyRes = await fetch(`${API_URL}/api/push/vapid-key`);
      const { public_key } = await keyRes.json();

      const reg = await registerServiceWorker();
      if (!reg) throw new Error('Service worker registration failed');

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });

      const subJson = sub.toJSON();
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          city: city?.split(',')[0] || '',
        }),
      });

      setSubscribed(true);
      setPermission('granted');
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [city, registerServiceWorker]);

  const unsubscribe = useCallback(async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`, {
        method: 'DELETE', credentials: 'include',
      });
      await sub.unsubscribe();
      setSubscribed(false);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      return 'denied';
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') await subscribe();
    return result;
  }, [subscribe]);

  const isSupported = typeof Notification !== 'undefined' && 'PushManager' in window && 'serviceWorker' in navigator;

  return { permission, subscribed, loading, isSupported, requestPermission, unsubscribe };
};
